import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import TombstoneScene from "../components/TombstoneScene";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.7, ease: "easeOut" },
};

export default function Home() {
  return (
    <div className="page-enter" data-testid="home-page">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="fog-layer" />
        <div className="grain relative max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-24 md:pt-20 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div
              className="lg:col-span-5 order-2 lg:order-1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            >
              <div className="overline" data-testid="hero-overline">
                Film · Animation · Design
              </div>
              <h1
                className="font-display mt-4 text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
                style={{ color: "var(--gl-text)" }}
                data-testid="hero-heading"
              >
                Where stories
                <br />
                are <em style={{ color: "var(--gl-amber)", fontStyle: "italic" }}>unearthed</em>.
              </h1>
              <p
                className="mt-6 max-w-md text-base leading-relaxed"
                style={{ color: "var(--gl-muted)" }}
                data-testid="hero-subtext"
              >
                GraveLight Studio is a film & design house that digs where others daren't — pulling
                narratives out of fog, folklore, and forgotten things, then polishing them until
                they glow like lanternlight.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/projects" className="btn-amber" data-testid="hero-cta-projects">
                  What we offer
                </Link>
                <Link to="/contact" className="btn-ghost" data-testid="hero-cta-contact">
                  Commission a story
                </Link>
              </div>

              <div className="mt-10 flex items-center gap-6" data-testid="hero-marquee">
                <div className="overline">Now accepting</div>
                <div className="flex items-center gap-5 text-sm flex-wrap" style={{ color: "var(--gl-muted)" }}>
                  <span>Founding commissions</span>
                  <span>·</span>
                  <span>Live event bookings</span>
                </div>
              </div>
            </motion.div>

            <div className="lg:col-span-7 order-1 lg:order-2 relative">
              <TombstoneScene />
            </div>
          </div>
        </div>
      </section>

      {/* ===== MANIFESTO STRIP ===== */}
      <section className="py-20 md:py-28 border-y" style={{ borderColor: "var(--gl-border)" }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <motion.p
            {...fadeUp}
            className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight"
            style={{ color: "var(--gl-text)" }}
            data-testid="manifesto-text"
          >
            We create <em style={{ color: "var(--gl-amber)" }}>cinematic worlds</em> for film,
            live events, and bold brands — designed to hold attention and leave a lasting{" "}
            <span className="underline decoration-1 underline-offset-8" style={{ textDecorationColor: "var(--gl-amber)" }}>impact</span>.
          </motion.p>
          <div className="mt-10 rune-divider"><span className="overline">Our craft</span></div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Film & Animation", body: "Shorts, title sequences, music films. Grain, patience, candlelight." },
              { title: "Brand Identity", body: "Expressive wordmarks, visual systems, typography with weight and character." },
              { title: "Story Development", body: "We read your draft like a séance. Then we write toward the thing you almost said." },
              { title: "Live Event Coverage", body: "Raves, deep dubstep / drum and bass nights, house sets, concerts, festivals, brand activations — shot in cinematic low-light with reverence for atmosphere." },
            ].map((s, i) => (
              <motion.div
                key={s.title}
                {...fadeUp}
                transition={{ duration: 0.7, delay: i * 0.1, ease: "easeOut" }}
                className="p-6 gl-card"
                data-testid={`craft-card-${i}`}
              >
                <div className="overline mb-2">0{i + 1}</div>
                <div className="font-display text-2xl mb-2">{s.title}</div>
                <p className="text-sm" style={{ color: "var(--gl-muted)" }}>{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES CALLOUT (no portfolio yet) ===== */}
      <section className="py-20 md:py-28" data-testid="home-services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="overline">What we offer</div>
              <h2 className="font-display text-4xl sm:text-5xl mt-3">Four doors into the fog</h2>
            </div>
            <Link to="/contact" className="gl-link text-sm tracking-[0.2em] uppercase" data-testid="home-services-cta">
              Brief us →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: "Film & Animation",
                body: "Short films, title sequences, music videos, animated vignettes. Whether your story lives in light or shadow, we craft it to fit your vision.",
                tag: "Cinema",
              },
              {
                title: "Brand Identity",
                body: "Wordmarks, logo systems, editorial palettes. Built for labels, publishers, and studios who want presence, not polish.",
                tag: "Design",
              },
              {
                title: "Story Development",
                body: "Script notes, narrative consultation, treatment writing. We help you find the second draft hiding under the first.",
                tag: "Writing",
              },
              {
                title: "Live Event Coverage",
                body: "Cinematic capture of raves, deep dubstep nights, house music sets, concerts, festivals, brand activations, and marketing experiences — shot for low light, smoke, strobe, and the energy that lives between the drops.",
                tag: "On Location",
              },
            ].map((s, i) => (
              <motion.article
                key={s.title}
                {...fadeUp}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="gl-card p-8 flex flex-col justify-between min-h-[200px]"
                data-testid={`home-service-${i}`}
              >
                <div>
                  <div className="overline mb-2">{s.tag}</div>
                  <div className="font-display text-3xl leading-tight">{s.title}</div>
                  <p className="text-sm mt-3" style={{ color: "var(--gl-muted)" }}>{s.body}</p>
                </div>
                <div className="mt-6">
                  <Link to="/contact" className="gl-link text-xs tracking-[0.25em] uppercase" data-testid={`home-service-cta-${i}`}>
                    Enquire →
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-24 text-center" data-testid="home-cta-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="overline">A séance, but for your brief</div>
          <h3 className="font-display text-4xl sm:text-5xl mt-4">Have a story that won't stay quiet?</h3>
          <p className="mt-4" style={{ color: "var(--gl-muted)" }}>
            Tell us what's haunting you. We'll bring the shovel.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn-amber" data-testid="home-cta-contact-btn">Begin the unearthing</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
